import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Avatar from 'antd/lib/avatar';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Tooltip from 'antd/lib/tooltip';
import { SwapOutlined } from '@ant-design/icons';
import { RootState } from '../../reducers/reducers';
import { selectOrg, selectIsStatelessServer, selectUserIsRunwayAdmin } from '../../selectors/selectors';
import { createOrgRoute } from '../../helpers/routeHelpers';
import routes from '../../routes';
import { displayUserNameShort } from '../../helpers/displayHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { useSidebarColors } from '../styles/themeMode';
import styles from '../styles/organizationNavigatorStyles';
import ChangeOrgModal from '../userSettings/ChangeOrgModal';

export interface NavigationFooterProps {
    orgId: string | null;
}

export const NavigationFooter: React.FC<NavigationFooterProps> = ({ orgId }) => {
    const org = useSelector((state: RootState) => (orgId ? selectOrg(state, orgId) : null));
    const user = useSelector((state: RootState) => state.user);
    const isStatelessServer = useSelector((state: RootState) => selectIsStatelessServer(state));
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));
    const currentOrgId = useSelector((state: RootState) => selectOrg(state)?.id ?? null);

    const orgRoute = orgId ? createOrgRoute(orgId) : null;
    const orgLetter = org?.name?.charAt(0).toUpperCase() || 'O';
    const avatarDisplayName = displayUserNameShort(user).charAt(0).toUpperCase();

    const showChangeOrg = isStatelessServer && isRunwayAdmin;
    const [showChangeOrgModal, setShowChangeOrgModal] = React.useState(false);

    const sc = useSidebarColors();

    return (
        <div className="sidebar-footer-container">
            <div className="sidebar-footer-pill">
                {orgRoute && (
                    <>
                        {currentOrgId && showChangeOrg && (
                            <ChangeOrgModal
                                open={showChangeOrgModal}
                                currentOrgId={currentOrgId}
                                onCancel={() => setShowChangeOrgModal(false)}
                                onSuccess={() => setShowChangeOrgModal(false)}
                            />
                        )}
                        <Flex align="center" gap={12} style={{ padding: '10px', flex: 1, minWidth: 0 }}>
                            <Link
                                to={orgRoute}
                                style={{
                                    textDecoration: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                <div
                                    style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        backgroundColor: runwayColors.white,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        border: `1px solid ${antGrey.grey5}`,
                                    }}
                                >
                                    <Avatar size={20} src={org?.orgImageUrl} style={{ borderRadius: '50%' }}>
                                        {orgLetter}
                                    </Avatar>
                                </div>
                                <Text
                                    style={{
                                        color: sc.text,
                                        fontSize: 16,
                                        flex: 1,
                                        minWidth: 0,
                                        display: 'block',
                                    }}
                                    ellipsis
                                >
                                    {org?.name}
                                </Text>
                            </Link>
                            {showChangeOrg && (
                                <Tooltip title="Switch org">
                                    <Button
                                        type="text"
                                        icon={<SwapOutlined style={{ fontSize: 24 }} />}
                                        style={{ padding: 0, height: 'auto', color: sc.textMuted }}
                                        onClick={() => setShowChangeOrgModal(true)}
                                    />
                                </Tooltip>
                            )}
                        </Flex>
                    </>
                )}

                <Link to={routes.user} className="sidebar-footer-user">
                    <div className="sidebar-footer-user-outer">
                        <Avatar
                            size={28}
                            src={user.commsAvatars?.image48}
                            style={{
                                ...styles.userAvatar,
                                border: `1px solid ${runwayColors.white}`,
                            }}
                        >
                            {avatarDisplayName}
                        </Avatar>
                    </div>
                </Link>
            </div>
        </div>
    );
};
